import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";

import type { GroupRegistryDocument } from "./groupRegistry.document";
import {
  type CreateGroupRegistryRepositoryOptions,
  createGroupRegistryRepository,
  type GroupRegistryRepository,
} from "./groupRegistry.repository";

export interface GroupRegistryController
  extends Omit<
    GroupRegistryRepository,
    "getSnapshot" | "subscribe" | "dispose" | "isDisposed"
  > {
  document: GroupRegistryDocument;
  getSnapshot: GroupRegistryRepository["getSnapshot"];
  subscribe: GroupRegistryRepository["subscribe"];
}

const GroupRegistryContext = createContext<GroupRegistryController | null>(
  null,
);

export type GroupRegistryProviderProps = PropsWithChildren &
  CreateGroupRegistryRepositoryOptions;

const useGroupRegistryController = (
  repository: GroupRegistryRepository,
): GroupRegistryController => {
  const document = useSyncExternalStore(
    repository.subscribe,
    repository.getSnapshot,
  );

  return useMemo(
    () => ({
      document,
      createGroup: repository.createGroup,
      deleteGroup: repository.deleteGroup,
      updateGroup: repository.updateGroup,
      setGroupName: repository.setGroupName,
      setGroupColor: repository.setGroupColor,
      setGroupSymbol: repository.setGroupSymbol,
      setGroupDateRange: repository.setGroupDateRange,
      flush: repository.flush,
      getSnapshot: repository.getSnapshot,
      subscribe: repository.subscribe,
    }),
    [document, repository],
  );
};

const GroupRegistryConnected = ({
  children,
  repository,
}: PropsWithChildren<{ repository: GroupRegistryRepository }>) => {
  const value = useGroupRegistryController(repository);
  return (
    <GroupRegistryContext.Provider value={value}>
      {children}
    </GroupRegistryContext.Provider>
  );
};

export const GroupRegistryProvider = ({
  children,
  channel,
  createChannel,
  lock,
  storage,
  writerId,
}: GroupRegistryProviderProps) => {
  const [repository, setRepository] = useState<GroupRegistryRepository | null>(
    null,
  );

  useEffect(() => {
    const created = createGroupRegistryRepository({
      channel,
      createChannel,
      lock,
      storage,
      writerId,
    });
    setRepository(created);
    return () => created.dispose();
  }, [channel, createChannel, lock, storage, writerId]);

  if (!repository || repository.isDisposed()) {
    return null;
  }

  return (
    <GroupRegistryConnected repository={repository}>
      {children}
    </GroupRegistryConnected>
  );
};

export const useGroupRegistry = (): GroupRegistryController => {
  const value = useContext(GroupRegistryContext);
  if (!value) {
    throw new Error(
      "useGroupRegistry must be used within GroupRegistryProvider",
    );
  }
  return value;
};

export const useOptionalGroupRegistry = (): GroupRegistryController | null =>
  useContext(GroupRegistryContext);
